import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DocumentHistory } from "@/components/admin/DocumentHistory";

export const EditBookingDialog = ({ booking, onClose, onSave, onGenerateQuote, onGenerateInvoice }) => {
  const [formData, setFormData] = useState({
    customer_name: booking.customer_name,
    customer_email: booking.customer_email,
    customer_phone: booking.customer_phone,
    event_location: booking.event_location,
    event_type: booking.event_type || "",
    event_date: booking.event_date,
    event_start_time: booking.event_start_time || "",
    package_id: booking.package_id,
    payment_method: booking.payment_method,
    payment_status: booking.payment_status,
    description: booking.description || "",
    options: booking.options || {},
    promotion: booking.promotion || null,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(booking.id, formData);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifier la réservation</DialogTitle>
          <DialogDescription>Modification de la réservation #{booking.id}</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name">Nom du client</Label>
              <Input id="customer_name" value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} data-testid="edit-customer-name" required />
            </div>
            <div>
              <Label htmlFor="customer_email">Email</Label>
              <Input id="customer_email" type="email" value={formData.customer_email} onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })} data-testid="edit-customer-email" required />
            </div>
            <div>
              <Label htmlFor="customer_phone">Téléphone</Label>
              <Input id="customer_phone" value={formData.customer_phone} onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })} data-testid="edit-customer-phone" required />
            </div>
            <div>
              <Label htmlFor="event_date">Date de l'événement</Label>
              <Input id="event_date" type="date" value={formData.event_date} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} data-testid="edit-event-date" required />
            </div>
            <div>
              <Label htmlFor="event_start_time">Heure de début</Label>
              <Input id="event_start_time" type="time" value={formData.event_start_time} onChange={(e) => setFormData({ ...formData, event_start_time: e.target.value })} data-testid="edit-event-start-time" />
            </div>
            <div>
              <Label htmlFor="event_type">Type d'événement</Label>
              <Input id="event_type" value={formData.event_type} onChange={(e) => setFormData({ ...formData, event_type: e.target.value })} data-testid="edit-event-type" />
            </div>
            <div>
              <Label htmlFor="package_id">Forfait</Label>
              <select id="package_id" value={formData.package_id} onChange={(e) => setFormData({ ...formData, package_id: e.target.value })} className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md" data-testid="edit-package-id" required>
                <option value="3h">Forfait 3 heures (270€)</option>
                <option value="5h">Forfait 5 heures (340€)</option>
                <option value="7h">Forfait 7 heures (400€)</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="event_location">Lieu</Label>
              <Input id="event_location" value={formData.event_location} onChange={(e) => setFormData({ ...formData, event_location: e.target.value })} data-testid="edit-event-location" required />
            </div>
            
            {/* Options */}
            <div className="sm:col-span-2">
              <Label className="mb-2 block">Options</Label>
              <div className="space-y-2 border rounded-md p-3 bg-gray-50">
                <label className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    checked={formData.options.magnet_50 || false}
                    onChange={(e) => setFormData({ ...formData, options: { ...formData.options, magnet_50: e.target.checked } })}
                    className="rounded"
                  />
                  <span className="text-sm">Support Photo Aimanté (50 unités) - 50€</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    checked={formData.options.magnet_100 || false}
                    onChange={(e) => setFormData({ ...formData, options: { ...formData.options, magnet_100: e.target.checked } })}
                    className="rounded"
                  />
                  <span className="text-sm">Support Photo Aimanté (100 unités) - 80€</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input 
                    type="checkbox" 
                    checked={formData.options.backdrop || false}
                    onChange={(e) => setFormData({ ...formData, options: { ...formData.options, backdrop: e.target.checked } })}
                    className="rounded"
                  />
                  <span className="text-sm">Fond pour Photos - 40€</span>
                </label>
              </div>
            </div>
            
            {/* Promotion Manual Override */}
            <div className="sm:col-span-2 border rounded-md p-4 bg-rose-50">
              <Label className="mb-3 block font-semibold text-rose-700">🎁 Promotion Manuelle</Label>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="promo_title" className="text-sm">Titre</Label>
                  <Input 
                    id="promo_title" 
                    value={formData.promotion?.title || ""} 
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      promotion: { 
                        ...formData.promotion, 
                        title: e.target.value,
                        type: formData.promotion?.type || "percentage",
                        value: formData.promotion?.value || 0
                      } 
                    })}
                    placeholder="ex: Promotion Spéciale"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="promo_type" className="text-sm">Type</Label>
                  <select 
                    id="promo_type" 
                    value={formData.promotion?.type || "percentage"} 
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      promotion: { 
                        ...formData.promotion, 
                        title: formData.promotion?.title || "",
                        type: e.target.value,
                        value: formData.promotion?.value || 0
                      } 
                    })}
                    className="w-full h-9 px-2 py-1 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="percentage">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (€)</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="promo_value" className="text-sm">Valeur</Label>
                  <Input 
                    id="promo_value" 
                    type="number" 
                    step="0.01"
                    value={formData.promotion?.value || ""} 
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      promotion: { 
                        ...formData.promotion, 
                        title: formData.promotion?.title || "",
                        type: formData.promotion?.type || "percentage",
                        value: parseFloat(e.target.value) || 0
                      } 
                    })}
                    placeholder="0"
                    className="text-sm"
                  />
                </div>
              </div>
              {formData.promotion?.title && formData.promotion?.value > 0 && (
                <div className="mt-2 p-2 bg-white rounded border border-rose-200">
                  <p className="text-sm text-rose-700 font-semibold">
                    📢 {formData.promotion.title} : -
                    {formData.promotion.type === "percentage" 
                      ? `${formData.promotion.value}%` 
                      : `${formData.promotion.value}€`}
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-600 mt-2">
                💡 Cette promotion remplacera la promotion globale active pour cette réservation
              </p>
            </div>
            
            <div>
              <Label htmlFor="payment_method">Mode de règlement</Label>
              <select id="payment_method" value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md" data-testid="edit-payment-method" required>
                <option value="whatsapp">WhatsApp</option>
                <option value="stripe">Stripe (Carte bancaire)</option>
              </select>
            </div>
            <div>
              <Label htmlFor="payment_status">Statut de paiement</Label>
              <select id="payment_status" value={formData.payment_status} onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })} className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md" data-testid="edit-payment-status" required>
                <option value="pending">En attente de confirmation</option>
                <option value="confirmed">Réservation effectuée</option>
                <option value="paid">Payée</option>
              </select>
            </div>
            
            {/* Description */}
            <div className="sm:col-span-2">
              <Label htmlFor="description">Description / Notes</Label>
              <Textarea 
                id="description" 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                placeholder="Ajoutez des notes ou une description pour cette réservation..."
                rows={4}
                className="resize-none"
                data-testid="edit-description"
              />
            </div>
          </div>
          
          {/* Document History */}
          <DocumentHistory 
            booking={booking} 
            onRegenerateQuote={onGenerateQuote}
            onRegenerateInvoice={onGenerateInvoice}
          />
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="bg-gradient-to-r from-rose-500 to-pink-500" data-testid="save-booking-btn">Enregistrer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};